import React, { useEffect, useState, useRef } from 'react';
import { logger } from '../../utils/logger';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { EmailNotificationsConfig } from './EmailNotificationsConfig';
import { VerificationCodesConfig } from './VerificationCodesConfig';
import { UserManagement } from './UserManagement';
import { Roles } from './Roles';
import { Permissions } from './Permissions';
import { BackupConfig } from './BackupConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { hasPermission } from '../../types';
import { UserIcon, SettingsIcon, BellIcon, ShieldIcon, MailIcon, UsersIcon, KeyIcon, ShieldCheckIcon, CameraIcon, EyeIcon, ChevronRightIcon, DatabaseIcon } from 'lucide-react';
interface NotificationSettings {
  newAppointments: boolean;
  cancelledAppointments: boolean;
  newPatients: boolean;
}
interface Preferences {
  darkMode: boolean;
  desktopNotifications: boolean;
}
function ReadOnlyBanner() {
  return <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-blue-700 dark:text-blue-300 flex items-center gap-3 text-sm shadow-sm" data-id="element-5942" data-mp="2hdb0ojm6imv6">
      <EyeIcon className="w-5 h-5 flex-shrink-0" data-id="element-5943" data-mp="2qcy1b3733on6" />
      <span className="font-medium" data-id="element-5944" data-mp="2qbmzh8x78qyp">
        Solo lectura — No tienes permisos para editar esta sección.
      </span>
    </div>;
}
export function Settings() {
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const {
    user,
    updateUser,
    login
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const userRole = user?.role || '';
  // Permission checks
  const canEditProfile = hasPermission(userRole, 'settings_profile', 'full');
  const canEditPreferences = hasPermission(userRole, 'settings_preferences', 'full');
  const canEditNotifications = hasPermission(userRole, 'settings_notifications', 'full');
  const canEditSecurity = hasPermission(userRole, 'settings_security', 'full');
  const canEditEmail = hasPermission(userRole, 'settings_email', 'full');
  const canEditVerification = hasPermission(userRole, 'settings_verification', 'full');
  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaved, setProfileSaved] = useState(false);
  const [avatarSaved, setAvatarSaved] = useState(false);
  const [pendingAvatar, setPendingAvatar] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Sync local state when user changes in context (e.g., updated from another place)
  useEffect(() => {
    if (user?.name && user.name !== name && !profileSaved) {
      setName(user.name);
    }
    if (user?.email && user.email !== email && !profileSaved) {
      setEmail(user.email);
    }
  }, [user?.name, user?.email]);
  // Security state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  // Preferences state
  const [preferences, setPreferences] = useState<Preferences>({
    darkMode: theme === 'dark',
    desktopNotifications: false
  });
  // Notification settings state
  const [notifications, setNotifications] = useState<NotificationSettings>({
    newAppointments: true,
    cancelledAppointments: true,
    newPatients: true
  });
  // Active Tab State
  const [activeTabId, setActiveTabId] = useState('profile');
  // Load preferences
  useEffect(() => {
    const savedPrefs = localStorage.getItem('savidna-preferences');
    if (savedPrefs) {
      try {
        const parsed = JSON.parse(savedPrefs);
        setPreferences(parsed);
      } catch (e) {
        logger.error('Error loading preferences:', e);
      }
    }
    const savedNotifs = localStorage.getItem('savidna-notification-settings');
    if (savedNotifs) {
      try {
        const parsed = JSON.parse(savedNotifs);
        setNotifications(parsed);
      } catch (e) {
        logger.error('Error loading notification settings:', e);
      }
    }
  }, []);
  // Save preferences
  const savePreferences = (newPrefs: Preferences) => {
    setPreferences(newPrefs);
    localStorage.setItem('savidna-preferences', JSON.stringify(newPrefs));
  };
  // Save notification settings
  const saveNotifications = (newNotifs: NotificationSettings) => {
    setNotifications(newNotifs);
    localStorage.setItem('savidna-notification-settings', JSON.stringify(newNotifs));
  };
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen es demasiado grande. El tamaño máximo es 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setPendingAvatar(base64String);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSaveAvatar = () => {
    if (!pendingAvatar) return;
    if (updateUser) {
      updateUser({
        avatar: pendingAvatar
      });
    }
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          avatar: pendingAvatar
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
      } catch (e) {
        logger.error('Error saving avatar:', e);
      }
    }
    setPendingAvatar(null);
    setAvatarSaved(true);
    setTimeout(() => setAvatarSaved(false), 3000);
  };
  const handleSaveProfile = () => {
    if (!canEditProfile) return;
    if (!name || !email) {
      showToast({
        type: 'warning',
        title: 'Campos requeridos',
        message: 'Por favor completa todos los campos.'
      });
      return;
    }
    confirm({
      title: 'Guardar Perfil',
      message: '¿Estás seguro de que deseas guardar los cambios en tu perfil?',
      confirmText: 'Guardar',
      onConfirm: () => {
        if (updateUser) {
          updateUser({
            name,
            email
          });
        }
        const savedUsers = localStorage.getItem('savidna-users');
        if (savedUsers) {
          try {
            const users = JSON.parse(savedUsers);
            const updatedUsers = users.map((u: any) => u.id === user?.id ? {
              ...u,
              name,
              email
            } : u);
            localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
          } catch (e) {
            logger.error('Error updating user:', e);
          }
        }
        showToast({
          type: 'success',
          title: 'Perfil actualizado',
          message: 'Tu perfil fue actualizado correctamente.'
        });
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 3000);
      }
    });
  };
  const handleChangePassword = async () => {
    if (!canEditSecurity) return;
    setPasswordError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Por favor completa todos los campos');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    confirm({
      title: 'Cambiar Contraseña',
      message: '¿Estás seguro de que deseas cambiar tu contraseña?',
      confirmText: 'Cambiar',
      onConfirm: async () => {
        setIsChangingPassword(true);
        try {
          if (!user?.email) {
            setPasswordError('Error de sesión. Recarga la página.');
            setIsChangingPassword(false);
            return;
          }
          const loginResult = await login(user.email, currentPassword);
          if (!loginResult.success) {
            setPasswordError('La contraseña actual es incorrecta');
            setIsChangingPassword(false);
            return;
          }
          await updateUser({
            password: newPassword
          });
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setPasswordSaved(true);
          setTimeout(() => setPasswordSaved(false), 3000);
        } catch (e) {
          logger.error('Error updating password:', e);
          setPasswordError('Error al cambiar la contraseña');
        } finally {
          setIsChangingPassword(false);
        }
      }
    });
  };
  const handleToggleDarkMode = (checked: boolean) => {
    if (!canEditPreferences) return;
    savePreferences({
      ...preferences,
      darkMode: checked
    });
    toggleTheme();
  };
  const handleToggleDesktopNotifications = (checked: boolean) => {
    if (!canEditPreferences) return;
    savePreferences({
      ...preferences,
      desktopNotifications: checked
    });
    if (checked && 'Notification' in window) {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('Notificaciones activadas', {
            body: 'Ahora recibirás notificaciones de Savidna',
            icon: '/images.jpg'
          });
        }
      });
    }
  };
  // Define tabs content
  const allTabs = [{
    id: 'profile',
    label: 'Perfil',
    icon: UserIcon,
    permissionKey: 'settings_profile' as const,
    content: <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5945" data-mp="1p7yeo1g1yykn">
          {!canEditProfile && <ReadOnlyBanner data-id="element-5946" data-mp="1f7pcm6zdqixc" />}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-5947" data-mp="1f8h9dl4pqmp7">
            <div className="px-8 py-8" data-id="element-5948" data-mp="2cuwnqe1uf48j">
              <div className="relative flex flex-col sm:flex-row justify-between items-start mb-6 gap-4" data-id="element-5949" data-mp="27wmuwa3lcfq3">
                <div className="flex items-center gap-6" data-id="element-5950" data-mp="235d4366b36cm">
                  <div className="relative group" data-id="element-5951" data-mp="233tn1dctsr9s">
                    <div className="w-24 h-24 rounded-full border-4 border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-700 overflow-hidden relative shadow-md cursor-pointer" onClick={handleAvatarClick} data-id="element-5952" data-mp="1y7boiv839p5w">
                      {pendingAvatar || user?.avatar ? <img src={pendingAvatar || user?.avatar} alt={user?.name || ''} className="w-full h-full object-cover" data-id="element-5953" data-mp="2z5g26iqnc28e" /> : <div className="w-full h-full flex items-center justify-center bg-teal-50 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400 text-3xl font-bold" data-id="element-5954" data-mp="3d45z9ydgu1wr">
                          {name.charAt(0).toUpperCase()}
                        </div>}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-5955" data-mp="3qvb033skl08o">
                        <CameraIcon className="w-8 h-8 text-white" data-id="element-5956" data-mp="3vw8gpbxxlm82" />
                      </div>
                    </div>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} data-id="element-5957" data-mp="9mxh1dett01i" />
                  </div>
                  <div data-id="element-5958" data-mp="3b2yudhwrmz8z">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5959" data-mp="3ptzdqhz1wdjw">
                      {name}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400" data-id="element-5960" data-mp="3l27tsqaeh62q">{email}</p>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2" data-id="element-5961" data-mp="2nf5sw71i509">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 border border-teal-200 dark:border-teal-800 capitalize" data-id="element-5962" data-mp="3twcl1atg4jis">
                    {user?.role || 'Usuario'}
                  </span>
                  {pendingAvatar && <Button onClick={handleSaveAvatar} size="sm" data-id="element-5963" data-mp="2ef2lkz7kl88b">
                      <CameraIcon className="w-4 h-4 mr-1" data-id="element-5964" data-mp="29gre2v4qz9t9" />
                      Guardar Foto
                    </Button>}
                </div>
              </div>

              {avatarSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-300 flex items-center gap-2" data-id="element-5965" data-mp="tw8m1uc2p1up">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-5966" data-mp="k5nng6wefxbs" />
                  <span className="font-medium" data-id="element-5967" data-mp="67fjvg4uxxrn">
                    Foto de perfil actualizada correctamente
                  </span>
                </div>}

              {profileSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-300 flex items-center gap-2" data-id="element-5968" data-mp="3sexchy9ftxpo">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-5969" data-mp="1qnakpuhq1ys5" />
                  <span className="font-medium" data-id="element-5970" data-mp="1qntzx3fzm92h">
                    Perfil actualizado correctamente
                  </span>
                </div>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-5971" data-mp="3rpicih9v9x3t">
                <div className="space-y-5" data-id="element-5972" data-mp="3mqf88z1pnuww">
                  <Input label="Nombre Completo" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" disabled={!canEditProfile} data-id="element-5973" data-mp="3mrgvs2zwy9vi" />

                  <Input label="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" disabled={!canEditProfile} data-id="element-5974" data-mp="38qny59ebeyo6" />
                </div>
                <div className="space-y-5" data-id="element-5975" data-mp="2v0uc0yu72si5">
                  <Input label="Cargo / Rol" value={user?.role || 'Administrador'} disabled className="bg-gray-50 dark:bg-gray-900/50" data-id="element-5976" data-mp="2pzmzynl0r75o" />

                  {canEditProfile && <div className="pt-7" data-id="element-5977" data-mp="10l5a2zzb01kz">
                      <Button onClick={handleSaveProfile} className="w-full" data-id="element-5978" data-mp="15gt17kxcj4t4">
                        Guardar Cambios
                      </Button>
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: 'preferences',
    label: 'Preferencias',
    icon: SettingsIcon,
    permissionKey: 'settings_preferences' as const,
    content: <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5979" data-mp="mp07tped7hkv">
          <div className="space-y-6" data-id="element-5980" data-mp="qroj03qjx0l0">
            {!canEditPreferences && <ReadOnlyBanner data-id="element-5981" data-mp="vkq0mji57e8a" />}
            <div data-id="element-5982" data-mp="vkzfpc1ng0z7">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5983" data-mp="vlj0d0iop3gx">
                Preferencias del Sistema
              </h3>
              <div className="space-y-6" data-id="element-5984" data-mp="32lucbxi27h0">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-5985" data-mp="31aom6khupn9">
                  <div data-id="element-5986" data-mp="11e6jxto6h8br">
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-5987" data-mp="11ey1w1our7jk">
                      Modo Oscuro
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5988" data-mp="1b59iootmxb7v">
                      Activar tema oscuro para reducir la fatiga visual
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditPreferences ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-5989" data-mp="iglr6bialplv">
                    <input type="checkbox" checked={preferences.darkMode} onChange={e => handleToggleDarkMode(e.target.checked)} className="sr-only peer" disabled={!canEditPreferences} data-id="element-5990" data-mp="seje8vof1gcw" />

                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#1BACA6]" data-id="element-5991" data-mp="2bcof5bgo3azx"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-5992" data-mp="2z1h4uq6p4mm3">
                  <div data-id="element-5993" data-mp="2pch00q5iiwzz">
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-5994" data-mp="2pbxkth78ympn">
                      Notificaciones de Escritorio
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5995" data-mp="3ht9wag649zrp">
                      Recibir alertas emergentes en el navegador
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditPreferences ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-5996" data-mp="1hsv3pwv8v783">
                    <input type="checkbox" checked={preferences.desktopNotifications} onChange={e => handleToggleDesktopNotifications(e.target.checked)} className="sr-only peer" disabled={!canEditPreferences} data-id="element-5997" data-mp="18wm3ifzg04kw" />

                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#1BACA6]" data-id="element-5998" data-mp="1rrayc9thcymc"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'notifications',
    label: 'Notificaciones',
    icon: BellIcon,
    permissionKey: 'settings_notifications' as const,
    content: <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5999" data-mp="jwpudjt7ivlr">
          <div className="space-y-6" data-id="element-6000" data-mp="ovkmjcd2dwij">
            {!canEditNotifications && <ReadOnlyBanner data-id="element-6001" data-mp="tvp2n1a28p88" />}
            <div data-id="element-6002" data-mp="tvfab71jm5zd">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-6003" data-mp="tx058xrrrqxr">
                Configuración de Notificaciones
              </h3>
              <div className="space-y-4" data-id="element-6004" data-mp="2vnbnel3cznw9">
                {[{
              key: 'newAppointments',
              label: 'Nuevas Citas',
              desc: 'Notificar cuando se crea una nueva cita'
            }, {
              key: 'cancelledAppointments',
              label: 'Citas Canceladas',
              desc: 'Notificar cuando se cancela una cita'
            }, {
              key: 'newPatients',
              label: 'Nuevos Pacientes',
              desc: 'Notificar cuando se registra un nuevo paciente'
            }].map(item => <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-6005" data-mp="1wa4yzzv65kg2">
                    <div data-id="element-6006" data-mp="259s1kozmwwap">
                      <p className="font-semibold text-gray-900 dark:text-white" data-id="element-6007" data-mp="258z1u7z3f722">
                        {item.label}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-6008" data-mp="awinejw58mm4">
                        {item.desc}
                      </p>
                    </div>
                    <label className={`relative inline-flex items-center ${canEditNotifications ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-6009" data-mp="3fcantjapfar">
                      <input type="checkbox" checked={notifications[item.key as keyof NotificationSettings]} onChange={e => canEditNotifications && saveNotifications({
                  ...notifications,
                  [item.key]: e.target.checked
                })} className="sr-only peer" disabled={!canEditNotifications} data-id="element-6010" data-mp="3pqrdg7sh6wgj" />

                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#1BACA6]" data-id="element-6011" data-mp="2t2lf22m5d0ap"></div>
                    </label>
                  </div>)}
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'security',
    label: 'Seguridad',
    icon: ShieldIcon,
    permissionKey: 'settings_security' as const,
    content: <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6012" data-mp="26twif3dbjeap">
          <div className="space-y-6" data-id="element-6013" data-mp="1sun0nz9gsc4m">
            {!canEditSecurity && <ReadOnlyBanner data-id="element-6014" data-mp="1sv6k5kerj8kr" />}
            <div data-id="element-6015" data-mp="1sx0xacjslwea">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-6016" data-mp="1ww08ug014rep">
                Seguridad de la Cuenta
              </h3>

              {passwordSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-600 dark:text-green-400 text-sm flex items-center gap-2" data-id="element-6017" data-mp="1e9sp7kls676j">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-6018" data-mp="1n69va9fafevf" />
                  <span className="font-medium" data-id="element-6019" data-mp="1i8ruj6bfc5yi">
                    Contraseña actualizada correctamente
                  </span>
                </div>}

              {passwordError && <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm flex items-center gap-2" data-id="element-6020" data-mp="1s77k34usz75w">
                  <ShieldIcon className="w-5 h-5" data-id="element-6021" data-mp="21ytsyujo4u5h" />
                  <span className="font-medium" data-id="element-6022" data-mp="2fxbcsh6gdmi4">{passwordError}</span>
                </div>}

              {canEditSecurity ? <div className="space-y-5 max-w-lg" data-id="element-6023" data-mp="2azjkvn5t60dx">
                  <Input label="Contraseña Actual" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-6024" data-mp="2v0bcn5eyu5bn" />

                  <Input label="Nueva Contraseña" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-6025" data-mp="33uzjv143px33" />

                  <Input label="Confirmar Nueva Contraseña" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-6026" data-mp="2g3c8xsn8iwmg" />

                  <div className="pt-2" data-id="element-6027" data-mp="bofw8bcdo8x">
                    <Button onClick={handleChangePassword} isLoading={isChangingPassword} data-id="element-6028" data-mp="5arb711isg3p">
                      Cambiar Contraseña
                    </Button>
                  </div>
                </div> : <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50 text-center" data-id="element-6029" data-mp="1g38s4j8rj3dt">
                  <ShieldIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" data-id="element-6030" data-mp="1b34d6xni6gpp" />
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-6031" data-mp="173lt243ia9nx">
                    No tienes permisos para cambiar la contraseña. Contacta a un
                    administrador.
                  </p>
                </div>}
            </div>
          </div>
        </Card>
  }, {
    id: 'email',
    label: 'Correos',
    icon: MailIcon,
    permissionKey: 'settings_email' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6032" data-mp="2rgb6d7ne3gut">
          <EmailNotificationsConfig isReadOnly={!canEditEmail} data-id="element-6033" data-mp="2mk40tfywo7de" />
        </div>
  }, {
    id: 'verification',
    label: 'Verificación',
    icon: ShieldCheckIcon,
    permissionKey: 'settings_verification' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6034" data-mp="awh8tbuuzwj6">
          <VerificationCodesConfig isReadOnly={!canEditVerification} data-id="element-6035" data-mp="fusm5wkd4p65" />
        </div>
  }, {
    id: 'users',
    label: 'Usuarios',
    icon: UsersIcon,
    permissionKey: 'settings_users' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6036" data-mp="18ieo70dxzlqw">
          <UserManagement data-id="element-6037" data-mp="ukq3bsjvkelu" />
        </div>
  }, {
    id: 'roles-permissions',
    label: 'Roles y Permisos',
    icon: KeyIcon,
    permissionKey: 'settings_roles_permissions' as const,
    content: <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6038" data-mp="3f9e19kgvsjow">
          <Roles data-id="element-6039" data-mp="3jck8ovfqcgwy" />
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8" data-id="element-6040" data-mp="3jd3q8b1gx3ag">
            <Permissions data-id="element-6041" data-mp="3bqyiisd7cdwf" />
          </div>
        </div>
  }, {
    id: 'backups',
    label: 'Backups',
    icon: DatabaseIcon,
    permissionKey: 'settings_backups' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6042" data-mp="3lpxkjs3gpbag">
          <BackupConfig data-id="element-6043" data-mp="3gsnx2hzmv9p9" />
        </div>
  }];
  // Filter tabs based on user permissions
  const tabs = allTabs.filter(tab => hasPermission(userRole, tab.permissionKey, 'read'));
  // Set default tab if current is invalid
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTabId)) {
      setActiveTabId(tabs[0].id);
    }
  }, [tabs, activeTabId]);
  const activeTabContent = tabs.find(t => t.id === activeTabId)?.content;
  return <div className="flex flex-col gap-6 min-h-0" data-id="element-6044" data-mp="2aah6khj7mxfk">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl shadow-xl" style={{
      background: 'linear-gradient(135deg, #0f766e, #0d9488, #0891b2)'
    }} text-white data-id="element-6045" data-mp="25cfo9ytlz255">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 19px,rgba(255,255,255,1) 19px,rgba(255,255,255,1) 20px),repeating-linear-gradient(90deg,transparent,transparent 19px,rgba(255,255,255,1) 19px,rgba(255,255,255,1) 20px)'
      }} data-id="element-6046" data-mp="1osheu7dc6m8t" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-6047" data-mp="27oqp0jl4asnj" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-6048" data-mp="2hgwixyuxjk5d" />
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 65%)'
      }} data-id="element-6049" data-mp="2lizsuj1yagoj" />
        <div className="absolute -bottom-16 -left-8 w-64 h-64 rounded-full opacity-15 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(6,182,212,0.8) 0%, transparent 60%)'
      }} data-id="element-6050" data-mp="erlfzsc3pz1h" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-6051" data-mp="vre3t9qw9xv" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-6052" data-mp="xj5ujqfeg6vw">
          <div data-id="element-6053" data-mp="tjnhfgttl03m">
            <div className="flex items-center gap-3 mb-2" data-id="element-6054" data-mp="tl85cnlmpksi">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm shadow-inner" data-id="element-6055" data-mp="oqud2u29jts2">
                <SettingsIcon className="w-6 h-6 text-white" data-id="element-6056" data-mp="jssmzp6f6u73" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-white" data-id="element-6057" data-mp="1h984obx0ajip">
                Configuración
              </h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-6058" data-mp="14bibduoae6y3">
              Administra tu cuenta, preferencias y seguridad del sistema
            </p>
          </div>
        </div>
      </div>

      {tabs.length > 0 ? <div className="flex flex-col md:flex-row gap-4 md:gap-8" data-id="element-6059" data-mp="1e9y3o0z1vm96">
          {/* Sidebar Navigation */}
          <nav className="w-full lg:w-72 shrink-0 flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0 px-1" data-id="element-6060" data-mp="19abo3kvr1tx7">
            {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTabId === tab.id;
          return <button key={tab.id} onClick={() => setActiveTabId(tab.id)} className={`
                    flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200
                    whitespace-nowrap md:whitespace-normal
                    ${isActive ? 'bg-white dark:bg-gray-800 text-[#1BACA6] shadow-md border border-gray-100 dark:border-gray-700 translate-x-1' : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-200'}
                  `} data-id="element-6061" data-mp="1wzwa6cn797ap">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-[#1BACA6]' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-6062" data-mp="2bltdp2jxzkqa" />

                  <span className="flex-1 text-left" data-id="element-6063" data-mp="21x31kxv6ugdk">{tab.label}</span>
                  {isActive && <ChevronRightIcon className="w-4 h-4 opacity-50 hidden md:block" data-id="element-6064" data-mp="26nrvsky95rge" />}
                </button>;
        })}
          </nav>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto pr-2 pb-10 custom-scrollbar" data-id="element-6065" data-mp="1xv81fwozu91d">
            {activeTabContent}
          </div>
        </div> : <Card data-id="element-6066" data-mp="1iyqrn9wgd2gx">
          <div className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-6067" data-mp="1j0a7ize75bi6">
            <SettingsIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" data-id="element-6068" data-mp="1nwsbhx1pd5tg" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-6069" data-mp="14u8ai8h5gtyd">
              Acceso Restringido
            </h3>
            <p data-id="element-6070" data-mp="10040fgcl85t2">No tienes permisos para ver ninguna sección de configuración.</p>
          </div>
        </Card>}
    </div>;
}